import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDemo } from '../../context/AppDemoContext';
import { FiEdit, FiX, FiCheck, FiArrowLeft, FiArrowRight, FiVideo, FiPenTool } from 'react-icons/fi';
import { phoneOutlineSvg } from '../../assets/images/phone-outline';

/**
 * Review slide that shows a summary of all selected options
 */
const ReviewSlide = () => {
    const navigate = useNavigate();
    const {
        // App data
        brandData,
        contentCategories,
        selectedBooks,
        splashVideo,
        primaryColor,
        secondaryColor,
        selectedLogo,
        appName,
        setAppName,
        bookQueries,
        // Material theme data
        selectedPalette,
        // Navigation
        prevStep,
        nextStep,
        goToStep,
    } = useAppDemo();

    // Enhanced debugging for all context values
    useEffect(() => {
        // Debug all important values from context
        console.log("======= ReviewSlide Debug Information =======");
        console.log("App name:", appName, typeof appName);
        console.log("Splash video:", splashVideo);
        console.log("Splash video type:", typeof splashVideo);
        if (splashVideo) {
            console.log("Video keys:", Object.keys(splashVideo));
            console.log("Video properties:", {
                id: splashVideo.id,
                title: splashVideo.title,
                author: splashVideo.author || splashVideo.channelTitle,
                thumbnailUrl: splashVideo.thumbnailUrl,
                thumbnail: splashVideo.thumbnail,
                image: splashVideo.image,
            });
        }
        console.log("Content categories:", contentCategories);
        console.log("Content categories array check:", Array.isArray(contentCategories));
        if (contentCategories && contentCategories.length > 0) {
            console.log("First content category:", contentCategories[0]);
            console.log("First content category type:", typeof contentCategories[0]);
            if (typeof contentCategories[0] === 'object') {
                console.log("First content category keys:", Object.keys(contentCategories[0]));
            }
        }
        console.log("Selected logo:", selectedLogo);
        console.log("Book queries:", bookQueries);
        console.log("Selected books:", selectedBooks);
        console.log("Primary color:", primaryColor);
        console.log("Secondary color:", secondaryColor);
        console.log("========================================");

        // Try to inspect what might cause the content categories to not display
        if (contentCategories) {
            try {
                const filtered = contentCategories.filter(category => {
                    console.log("Checking category:", category);
                    if (typeof category === 'string') {
                        console.log("- String category:", category);
                        return true;
                    }
                    
                    if (typeof category === 'object') {
                        console.log("- Object category:", category);
                        console.log("  - checked:", category.checked);
                        console.log("  - isChecked:", category.isChecked);
                        
                        if (category.checked === true) return true;
                        if (category.isChecked === true) return true;
                        if (category.checked === undefined && category.isChecked === undefined) return true;
                    }
                    
                    return false;
                });
                
                console.log("Filtered categories:", filtered);
                
                const mapped = filtered.map(category => {
                    if (typeof category === 'string') return category;
                    return category.name || category.label || category.text || '';
                });
                
                console.log("Mapped category names:", mapped);
            } catch (err) {
                console.error("Error analyzing content categories:", err);
            }
        }
    }, [appName, contentCategories, selectedLogo, bookQueries, splashVideo, selectedBooks, primaryColor, secondaryColor]);

    // State for editing app name
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState('');

    // Navigate to specific slides
    const goToWelcomeSlide = () => goToStep(1);
    const goToBrandStyleSlide = () => goToStep(2);
    const goToBookContentSlide = () => goToStep(3);
    const goToVideoSelectionSlide = () => goToStep(4);

    // Display helpers
    const displayAppName = () => {
        // Enhanced logging to debug the issue
        console.log("App name from context (type):", typeof appName);
        console.log("App name from context (value):", appName);
        
        // More robust check for app name
        if (appName !== undefined && appName !== null && appName !== "") {
            return appName;
        }
        
        // Try to get app name from brand data as a fallback
        if (brandData?.appSuggestions?.length > 0) {
            const suggestion = brandData.appSuggestions[0];
            if (suggestion.name_options?.length > 0) {
                return suggestion.name_options[0];
            }
            if (suggestion.name) {
                return suggestion.name;
            }
        }
        
        return "Not set";
    };

    // Handle app name edit
    const startEditingName = () => {
        setEditedName(appName || '');
        setIsEditingName(true);
    };

    const saveAppName = () => {
        if (editedName.trim()) {
            console.log("Saving app name:", editedName);
            setAppName(editedName.trim());
        }
        setIsEditingName(false);
    };

    const cancelAppNameEdit = () => {
        setIsEditingName(false);
    };

    // Display logo helper
    const displayLogo = () => {
        if (!selectedLogo) return null;
        
        // Handle different logo formats
        if (selectedLogo.imageData) {
            // Base64 encoded SVG
            return (
                <img 
                    src={`data:image/svg+xml;base64,${selectedLogo.imageData}`} 
                    alt="Logo" 
                    className="h-8 object-contain" 
                />
            );
        } else if (selectedLogo.src) {
            // Direct image URL
            return (
                <img 
                    src={selectedLogo.src} 
                    alt="Logo" 
                    className="h-8 object-contain" 
                />
            );
        }
        
        return "No logo available";
    };

    // Check if we have all required content 
    const allContentSelected = () => {
        // More lenient check for categories - if we can display categories, consider them selected
        const displayedCategories = getCheckedCategories();
        const hasCheckedCategories = contentCategories && (
            contentCategories.some(category => 
                typeof category === 'string' || category.checked || category.isChecked
            ) || displayedCategories.length > 0
        );
        
        // Debug what's missing
        console.log("Content Validation Status:", {
            appName: appName ? "✅ Set" : "❌ Missing",
            hasCheckedCategories: hasCheckedCategories ? `✅ Set (${displayedCategories.join(', ')})` : "❌ Missing",
            contentCategoriesFormat: contentCategories ? `Array[${contentCategories.length}]` : "Not set",
            selectedBooks: selectedBooks && selectedBooks.length > 0 ? `✅ Set (${selectedBooks.length} books)` : "❌ Missing",
            splashVideo: splashVideo ? "✅ Set" : "❌ Missing"
        });
        
        // For now, only require app name and splash video since book selection UI needs to be fixed
        // return appName && hasCheckedCategories && selectedBooks && selectedBooks.length > 0 && splashVideo;
        return appName && hasCheckedCategories && splashVideo;
    };

    // Helper to get checked categories
    const getCheckedCategories = () => {
        // Enhanced debugging for content categories
        console.log("Content categories in getCheckedCategories:", contentCategories);
        console.log("Content categories type:", typeof contentCategories);
        
        if (!contentCategories) return [];
        
        // Handle different possible formats of contentCategories data
        // Based on the screenshot, it appears the categories might be in a different format
        const result = contentCategories.filter(category => {
            // If it's a string, include it
            if (typeof category === 'string') return true;
            
            // If it's an object, include it if checked/isChecked is true or not specified
            if (typeof category === 'object') {
                // Include if explicitly checked
                if (category.checked === true || category.isChecked === true) return true;
                
                // Include if no checked/isChecked property (assume checked by default)
                if (category.checked === undefined && category.isChecked === undefined) return true;
                
                return false;
            }
            
            return false;
        }).map(category => {
            // Extract the name depending on the format
            if (typeof category === 'string') return category;
            return category.name || category.label || category.text || '';
        }).filter(name => name); // Filter out empty names
        
        console.log("Result from getCheckedCategories:", result);
        return result;
    };

    // Middle Column - Phone Preview 
    const renderPhonePreview = () => {
        const getVideoThumbnailUrl = () => {
            if (!splashVideo) {
                console.log("REVIEW: No selected video for thumbnail");
                return null;
            }
            
            // Log the full video object for debugging
            console.log("REVIEW: Getting thumbnail URL from video:", splashVideo);
            
            // Try all possible properties for the thumbnail URL
            const possibleProps = [
                'thumbnailUrl',
                'thumbnail',
                'image',
                'imageUrl',
                'imageURL',
                'preview',
                'poster'
            ];
            
            // Check each property
            for (const prop of possibleProps) {
                if (splashVideo[prop]) {
                    console.log(`REVIEW: Found thumbnail at '${prop}':`, splashVideo[prop]);
                    return splashVideo[prop];
                }
            }
            
            // Check nested properties
            if (splashVideo.video_files && splashVideo.video_files[0]?.preview) {
                return splashVideo.video_files[0].preview;
            }
            
            if (splashVideo.images && splashVideo.images.preview) {
                return splashVideo.images.preview;
            }
            
            console.log("REVIEW: No thumbnail URL found in video object");
            return null;
        };
        
        // Get the thumbnail URL
        const thumbnailUrl = getVideoThumbnailUrl();
        console.log("REVIEW: Final thumbnail URL:", thumbnailUrl);
        
        return (
            <div 
                className="col-span-1 flex flex-col items-center justify-center relative"
            >
                {/* Phone outline */}
                <div 
                    className="w-full max-w-xs flex justify-center items-center relative"
                    dangerouslySetInnerHTML={{ __html: phoneOutlineSvg }}
                />
                
                {/* Position the video inside the phone frame */}
                {splashVideo && thumbnailUrl && (
                    <div className="absolute inset-0 flex justify-center items-center">
                        <img 
                            src={thumbnailUrl}
                            alt={splashVideo.title || "Selected video"}
                            className="w-[240px] h-[520px] object-cover"
                            style={{
                                position: 'absolute',
                                top: '59px',
                                zIndex: 5,
                                borderRadius: '24px'
                            }}
                            onError={(e) => {
                                console.error("REVIEW: Video thumbnail load error in phone mockup:", e);
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                            }}
                        />
                        
                        {/* Gradient overlay to improve logo visibility */}
                        <div 
                            className="w-[240px] h-[520px]"
                            style={{
                                position: 'absolute',
                                top: '59px',
                                zIndex: 10,
                                borderRadius: '24px',
                                background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 30%, rgba(0,0,0,0.1) 70%, rgba(0,0,0,0) 100%)'
                            }}
                        />
                    </div>
                )}
                
                {/* Content overlaid on top */}
                <div className="absolute inset-0 flex flex-col justify-start items-center z-20 pointer-events-none pt-[100px]">
                    {/* Logo at the top of the phone screen */}
                    {selectedLogo && (
                        selectedLogo.imageData ? (
                            <img 
                                src={`data:image/svg+xml;base64,${selectedLogo.imageData}`} 
                                alt="Logo" 
                                className="h-12 object-contain mb-6" 
                                style={{ 
                                    maxWidth: '180px',
                                    maxHeight: '40px'
                                }}
                            />
                        ) : selectedLogo.src ? (
                            <img 
                                src={selectedLogo.src} 
                                alt="Logo" 
                                className="h-12 object-contain mb-6" 
                                style={{ 
                                    maxWidth: '180px',
                                    maxHeight: '40px'
                                }}
                            />
                        ) : null
                    )}
                    
                    {/* App name in a button */}
                    <button 
                        className="px-6 py-2 rounded-full text-white text-sm font-medium pointer-events-auto shadow-md"
                        style={{ backgroundColor: primaryColor || '#4A90E2' }}
                    >
                        {displayAppName()}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 h-full bg-gray-900 text-white">
            <h1 className="text-2xl font-bold mb-6 text-left">Review Your Selections</h1>
            
            <div className="grid grid-cols-3 gap-6 h-[calc(100%-4rem)]">
                {/* Left Column */}
                <div className="col-span-1 flex flex-col space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh-200px)' }}>
                    {/* App Information */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold">App Information</h3>
                            <button 
                                onClick={goToWelcomeSlide}
                                className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                            >
                                <FiEdit className="mr-1" /> Edit
                            </button>
                        </div>
                        <div className="flex flex-col space-y-2">
                            <div className="flex items-center">
                                <span className="text-gray-400 mr-2">Name:</span>
                                {isEditingName ? (
                                    <div className="flex items-center">
                                        <input 
                                            value={editedName} 
                                            onChange={(e) => setEditedName(e.target.value)}
                                            className="bg-gray-700 text-white px-2 py-1 rounded mr-2 text-sm"
                                            autoFocus
                                        />
                                        <button onClick={saveAppName} className="text-green-400 hover:text-green-300 mr-1">
                                            <FiCheck />
                                        </button>
                                        <button onClick={cancelAppNameEdit} className="text-red-400 hover:text-red-300">
                                            <FiX />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <span className="font-medium">{displayAppName()}</span>
                                        <button onClick={startEditingName} className="ml-2 text-blue-400 hover:text-blue-300">
                                            <FiEdit size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Brand Style */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold">Brand Style</h3>
                            <button 
                                onClick={goToBrandStyleSlide}
                                className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                            >
                                <FiEdit className="mr-1" /> Edit
                            </button>
                        </div>
                        <div className="flex flex-col space-y-2">
                            <div className="flex items-center">
                                <span className="text-gray-400 mr-2">Primary Color:</span>
                                <div 
                                    className="w-6 h-6 rounded-full border border-white/20" 
                                    style={{ backgroundColor: primaryColor }}
                                ></div>
                                <span className="ml-2 text-sm">{primaryColor || 'Not set'}</span>
                            </div>
                            <div className="flex items-center">
                                <span className="text-gray-400 mr-2">Secondary Color:</span>
                                <div 
                                    className="w-6 h-6 rounded-full border border-white/20" 
                                    style={{ backgroundColor: secondaryColor }}
                                ></div>
                                <span className="ml-2 text-sm">{secondaryColor || 'Not set'}</span>
                            </div>
                            <div className="flex items-center">
                                <span className="text-gray-400 mr-2">Logo:</span>
                                {displayLogo()}
                            </div>
                        </div>
                    </div>

                    {/* Content Categories */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold">Content Categories</h3>
                            <button 
                                onClick={goToWelcomeSlide}
                                className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                            >
                                <FiEdit className="mr-1" /> Edit
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {getCheckedCategories().length > 0 ? (
                                getCheckedCategories().map((categoryName, index) => (
                                    <span 
                                        key={index} 
                                        className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs flex items-center"
                                    >
                                        <FiCheck className="mr-1" size={14} />
                                        {categoryName}
                                    </span>
                                ))
                            ) : (
                                <span className="text-gray-400">No categories selected</span>
                            )}
                        </div>
                    </div>

                    {/* Color Palette */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold">Color Palette</h3>
                            <button 
                                onClick={goToBrandStyleSlide}
                                className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                            >
                                <FiEdit className="mr-1" /> Edit
                            </button>
                        </div>
                        <div className="mt-2">
                            {selectedPalette ? (
                                <div className="flex flex-col space-y-3">
                                    {/* Palette name */}
                                    <div className="text-sm text-white">
                                        {selectedPalette.name || "Selected Palette"}
                                    </div>
                                    
                                    {/* Compact color grid display */}
                                    <div className="grid grid-cols-4 gap-2">
                                        {selectedPalette.lightScheme && Object.entries(selectedPalette.lightScheme).map(([colorKey, colorValue], index) => (
                                            <div key={colorKey} className="flex flex-col items-center">
                                                <div 
                                                    className="w-6 h-6 rounded-md border border-gray-700 shadow-sm" 
                                                    style={{ backgroundColor: colorValue }}
                                                    title={colorKey}
                                                ></div>
                                                <span className="text-[8px] text-gray-400 mt-1 truncate w-full text-center">
                                                    {colorKey.replace(/([A-Z])/g, ' $1').trim()}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <span className="text-gray-400">No color palette selected</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Middle Column - Phone Preview */}
                <div className="col-span-1 flex justify-center items-start">
                    {renderPhonePreview()}
                </div>
                
                {/* Right Column */}
                <div className="col-span-1 flex flex-col space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh-200px)' }}>
                    {/* Book Content */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold">Book Content</h3>
                            <button 
                                onClick={goToBookContentSlide}
                                className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                            >
                                <FiEdit className="mr-1" /> Edit
                            </button>
                        </div>
                        <div className="flex flex-col space-y-2">
                            <div>
                                <span className="text-gray-400 mr-2">Search Terms:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {brandData?.organization?.name && (
                                        <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">
                                            {brandData.organization.name}
                                        </span>
                                    )}
                                    {bookQueries && bookQueries.length > 0 ? (
                                        bookQueries.map((query, index) => (
                                            <span key={index} className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">
                                                {query}
                                            </span>
                                        ))
                                    ) : getCheckedCategories().length > 0 ? (
                                        getCheckedCategories().map((categoryName, index) => (
                                            <span key={index} className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">
                                                {categoryName}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-gray-400">No search terms</span>
                                    )}
                                </div>
                            </div>
                            <div>
                                <span className="text-gray-400">Selected Books:</span>
                                <div className="mt-1">
                                    {selectedBooks && selectedBooks.length > 0 ? (
                                        <div className="flex flex-col space-y-1">
                                            {selectedBooks.map((book, index) => (
                                                <div key={index} className="text-sm">
                                                    {book.title}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">No books selected</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Video Content */}
                    <div className="bg-gray-800 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold">Video Content</h3>
                            <button 
                                onClick={goToVideoSelectionSlide}
                                className="text-blue-400 hover:text-blue-300 text-sm flex items-center"
                            >
                                <FiEdit className="mr-1" /> Edit
                            </button>
                        </div>
                        {splashVideo ? (
                            <div className="flex items-center">
                                {/* Use our improved thumbnail detection */}
                                {(() => {
                                    // Similar to our getVideoThumbnailUrl function
                                    const thumbnailUrl = splashVideo.thumbnailUrl || 
                                        splashVideo.thumbnail || 
                                        splashVideo.image ||
                                        splashVideo.imageUrl ||
                                        splashVideo.imageURL ||
                                        splashVideo.preview ||
                                        (splashVideo.video_files && splashVideo.video_files[0]?.preview) ||
                                        (splashVideo.images && splashVideo.images.preview);
                                    
                                    return thumbnailUrl ? (
                                        <img 
                                            src={thumbnailUrl} 
                                            alt={splashVideo.title || "Selected video"} 
                                            className="w-24 h-16 object-cover rounded mr-3"
                                            onError={(e) => {
                                                console.error("REVIEW: Video thumbnail load error in content section:", e);
                                                console.log("REVIEW: Video object for content section:", splashVideo);
                                                e.target.onerror = null;
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    ) : null;
                                })()}
                                <div className="flex flex-col">
                                    <div className="text-sm font-medium mb-1">{splashVideo.title || "Selected video"}</div>
                                    <div className="text-xs text-gray-400 mb-2">
                                        {splashVideo.author || splashVideo.channelTitle || "Unknown creator"}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <span className="text-gray-400">No video selected</span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewSlide;